import {
  LegalPageLayout,
  LegalSection,
  SUPPORT_EMAIL,
} from "./LegalPageLayout";

export function PrivacyPolicyPage() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      description="This policy explains how MediCorePay handles information when authorised pharmacy staff use the MediCorePay web service and mobile companion app."
    >
      <LegalSection title="1. Scope of this policy">
        <p>
          MediCorePay is a business service used by pharmacies and their
          authorised staff. The Android app is a companion to the pharmacy
          account: it supports dashboard access, stock lookup, inventory alerts,
          stock counts and receiving approvals. It is not a consumer health app
          and is not intended to provide medical advice.
        </p>
        <p>
          For clear Google Play identification, this policy covers the
          MediCorePay Android app with package ID{" "}
          <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-sm font-semibold text-slate-950">
            com.mobile.medicorepay
          </code>
          .
        </p>
        <p>
          A pharmacy or other subscribing organisation controls which staff can
          access its account and business records. Depending on the context,
          that organisation may be responsible for the business information
          processed through MediCorePay, while MediCorePay operates the service
          on its behalf.
        </p>
      </LegalSection>

      <LegalSection title="2. Information we handle">
        <ul className="list-disc space-y-3 pl-6 marker:text-blue-600">
          <li>
            <strong className="text-slate-950">
              Account and contact information:
            </strong>{" "}
            name, email address, phone number when supplied, role, permissions,
            sign-in information and session records.
          </li>
          <li>
            <strong className="text-slate-950">
              Organisation identifiers:
            </strong>{" "}
            pharmacy tenant, store, counter and user identifiers used to keep
            access and records within the correct business context.
          </li>
          <li>
            <strong className="text-slate-950">
              Inventory and receiving activity:
            </strong>{" "}
            product or barcode searches, stock and expiry information viewed,
            stock-count entries, receiving approval actions, timestamps and the
            audit history needed to attribute business actions.
          </li>
          <li>
            <strong className="text-slate-950">
              Device and app information:
            </strong>{" "}
            device or installation identifiers, operating system, app and API
            versions, network state, notification settings and related session
            information.
          </li>
          <li>
            <strong className="text-slate-950">
              Diagnostics and telemetry:
            </strong>{" "}
            error reports, reliability and performance events, request status,
            security events and technical logs used to operate and protect the
            service.
          </li>
          <li>
            <strong className="text-slate-950">Support communications:</strong>{" "}
            information included when a user or administrator contacts support,
            including the request and our response.
          </li>
        </ul>
        <p>
          The barcode scanner uses the device camera to recognise a barcode. The
          scanner feature does not require users to upload a photograph or video
          to MediCorePay.
        </p>
      </LegalSection>

      <LegalSection title="3. How we use information">
        <p>We use the information described above to:</p>
        <ul className="list-disc space-y-2 pl-6 marker:text-blue-600">
          <li>
            authenticate users and apply tenant, store, role and permission
            controls;
          </li>
          <li>
            provide sales and invoice review, inventory and barcode tools,
            purchasing and receiving, supplier management, reporting and other
            permission-aware pharmacy workflows requested by users;
          </li>
          <li>
            maintain security, fraud-prevention and business audit records;
          </li>
          <li>diagnose errors, monitor reliability and improve the service;</li>
          <li>respond to support, account and deletion requests; and</li>
          <li>
            meet contractual and legal obligations that apply to the service.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="4. How information is obtained">
        <p>
          Information comes from users, pharmacy administrators, activity within
          the service and the device or browser used to connect. The web app may
          use browser storage and similar technologies for session state and
          user preferences. MediCorePay does not use the app data described in
          this policy for third-party targeted advertising and does not sell it.
        </p>
      </LegalSection>

      <LegalSection title="5. When information is shared">
        <p>Information may be available to or shared with:</p>
        <ul className="list-disc space-y-2 pl-6 marker:text-blue-600">
          <li>
            the pharmacy organisation, its authorised administrators and staff,
            according to their permissions;
          </li>
          <li>
            service providers that host, secure, monitor, deliver or support
            MediCorePay, only for work performed on our behalf;
          </li>
          <li>
            professional advisers, authorities or other parties where disclosure
            is reasonably necessary to comply with law, protect rights or
            address security and misuse; and
          </li>
          <li>
            a successor in connection with a merger, financing, reorganisation
            or sale of the service, subject to appropriate confidentiality and
            legal requirements.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="6. Retention and deletion">
        <p>
          We retain account information while access is active and for a
          reasonable period afterwards for security, support and administration.
          Inventory, receiving, transaction and audit records are retained in
          accordance with the pharmacy organisation&apos;s instructions and
          applicable business, tax, regulatory and legal obligations. Diagnostic
          records are retained only as reasonably needed for security and
          service reliability. Backup copies are removed through normal backup
          rotation.
        </p>
        <p>
          Deleting an employee login does not necessarily delete pharmacy
          business records that identify who performed an action. See the{" "}
          <a
            className="font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4 hover:text-blue-900"
            href="/account-deletion"
          >
            account deletion page
          </a>{" "}
          for request steps and retention details.
        </p>
      </LegalSection>

      <LegalSection title="7. Security">
        <p>
          MediCorePay uses administrative, technical and organisational
          safeguards designed to protect information, including access controls,
          scoped user permissions, encrypted network connections and security
          monitoring. No storage or transmission method can be guaranteed
          completely secure. Users must protect their credentials and promptly
          report suspected unauthorised access.
        </p>
      </LegalSection>

      <LegalSection title="8. Choices and rights">
        <p>
          Subject to applicable law and the pharmacy organisation&apos;s
          obligations, a user may ask to access, correct or delete account
          information, or raise a concern about processing. Staff should
          normally contact their pharmacy administrator first because the
          administrator manages their work account. Requests may also be sent to{" "}
          <a
            className="font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4 hover:text-blue-900"
            href={`mailto:${SUPPORT_EMAIL}`}
          >
            {SUPPORT_EMAIL}
          </a>
          . We may need to verify identity and authority before acting on a
          request, and may refer a request concerning pharmacy-controlled
          records to the relevant organisation.
        </p>
      </LegalSection>

      <LegalSection title="9. Children">
        <p>
          MediCorePay is a pharmacy business service for authorised workforce
          users and is not directed to children. We do not knowingly offer
          accounts to children.
        </p>
      </LegalSection>

      <LegalSection title="10. Policy changes and contact">
        <p>
          We may update this policy as the service or legal requirements change.
          The effective and last-updated date at the top identifies the current
          version. Material changes will be communicated through an appropriate
          service or account channel.
        </p>
        <p>
          Questions or privacy requests can be sent to{" "}
          <a
            className="font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4 hover:text-blue-900"
            href={`mailto:${SUPPORT_EMAIL}?subject=MediCorePay%20privacy%20request`}
          >
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
